// pipeline-metrics.ts — Reforma UX §6.8 (wireframe 09 Pipelines).
//
// Helpers compartilhados entre PipelineCard e PipelineDetailModal:
//
// 1. successPct — PIPE-05: % sucesso = completos / (completos + falhos).
//    Runs CANCELADAS são terminais mas NÃO contam como falha (cancelar ≠
//    falhar) — ficam FORA do cálculo. Sem denominador → null (UI mostra "—").
//
// 2. normalizeSteps — o config jsonb de pipeline_versions tem 2 shapes vivos:
//    - canonical v2 (β2-A): { step_number, slug, name, executor:{agent_slug,
//      skill_slug?}, timeout_seconds, depends_on, ... }
//    - legacy: { id, name, kind, target_slug, handler_name, timeout_seconds }
//    A UI nova (etapas editáveis: executor agente/skill + timeout + retry)
//    precisa de um shape único — normalizamos aqui em vez de espalhar ifs.

import type { PipelineRunSummary, PipelineVersionSummary } from '@/lib/api-pipelines';
import type { RunCounts } from '@/lib/pipeline-counts';

/**
 * PIPE-05 — % sucesso = completos/(completos+falhos); cancelados FORA.
 * Retorna inteiro 0-100 ou null quando não há runs terminais (completos+falhos=0).
 */
export function successPct(completed: number, failed: number): number | null {
  const den = completed + failed;
  if (den === 0) return null;
  return Math.round((completed / den) * 100);
}

/** Formata successPct pra exibição ("94%" ou "—"). */
export function formatSuccessPct(counts: RunCounts | undefined): string {
  if (!counts) return '—';
  const pct = successPct(counts.completed, counts.failed);
  return pct === null ? '—' : `${pct}%`;
}

/**
 * Tempo médio do pipeline (wireframe: card "Tempo médio do pipeline" nas
 * Métricas) — média de duração dos runs COMPLETOS com started_at+completed_at.
 * Retorna string humana ("5m38s") ou null sem amostra.
 */
export function avgCompletedDuration(runs: PipelineRunSummary[]): string | null {
  const durations = runs
    .filter((r) => r.status === 'completed' && r.started_at && r.completed_at)
    .map((r) => new Date(r.completed_at as string).getTime() - new Date(r.started_at).getTime())
    .filter((ms) => Number.isFinite(ms) && ms >= 0);
  if (durations.length === 0) return null;
  const avgMs = durations.reduce((s, d) => s + d, 0) / durations.length;
  const totalSec = Math.round(avgMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

// ─── Steps (shape único pra UI de etapas editáveis) ──────────────────────

export interface NormalizedStep {
  /** Chave estável pra React (slug canonical v2 ou id legacy ou índice). */
  key: string;
  /** Posição 1-based na sequência. */
  stepNumber: number;
  name: string;
  /** Tipo de executor — wireframe: chip "agente · X" ou "skill · Y". */
  executorKind: 'agent' | 'skill';
  /** Slug do agente OU da skill executora. */
  executorSlug: string;
  timeoutSeconds: number | null;
  /** Retry on fail — canonical v2 não persiste hoje; null = não configurado. */
  retryOnFail: number | null;
}

interface CanonicalV2Step {
  step_number?: number;
  slug?: string;
  name?: string;
  executor?: { agent_slug?: string; skill_slug?: string };
  timeout_seconds?: number;
  retry_on_fail?: number;
}

interface LegacyStep {
  id?: string;
  name?: string;
  kind?: string;
  target_slug?: string;
  timeout_seconds?: number;
}

/**
 * Normaliza steps do config jsonb (canonical v2 OU legacy) pro shape da UI.
 * Defensivo: entradas malformadas não derrubam o modal — viram steps "—".
 */
export function normalizeSteps(config: Record<string, unknown> | undefined): NormalizedStep[] {
  const raw = config?.['steps'];
  if (!Array.isArray(raw)) return [];
  return raw.map((entry, i) => {
    const step = (entry ?? {}) as CanonicalV2Step & LegacyStep;
    // Canonical v2: executor.{agent_slug, skill_slug} — skill presente vence
    // (o agente invoca a skill; o chip do wireframe mostra a skill).
    const executor = step.executor;
    let executorKind: 'agent' | 'skill';
    let executorSlug: string;
    if (executor && typeof executor === 'object') {
      if (executor.skill_slug) {
        executorKind = 'skill';
        executorSlug = executor.skill_slug;
      } else {
        executorKind = 'agent';
        executorSlug = executor.agent_slug ?? '—';
      }
    } else {
      // Legacy: kind + target_slug.
      executorKind = step.kind === 'skill' ? 'skill' : 'agent';
      executorSlug = step.target_slug ?? '—';
    }
    return {
      key: step.slug ?? step.id ?? `step-${i}`,
      stepNumber: typeof step.step_number === 'number' ? step.step_number : i + 1,
      name: step.name ?? step.slug ?? step.id ?? `Etapa ${i + 1}`,
      executorKind,
      executorSlug,
      timeoutSeconds: typeof step.timeout_seconds === 'number' ? step.timeout_seconds : null,
      retryOnFail: typeof step.retry_on_fail === 'number' ? step.retry_on_fail : null,
    };
  });
}

/** Conta etapas da versão ativa (latest_version_id) — métrica "nº etapas" do card. */
export function countSteps(
  versions: PipelineVersionSummary[],
  latestVersionId: string | null,
): number | null {
  const active = versions.find((v) => v.id === latestVersionId) ?? versions[0];
  if (!active) return null;
  return normalizeSteps(active.config).length;
}
